/**
 * Live state of users/{uid}. Returns 'loading' until the doc resolves, then
 * 'present', or 'missing' only once the SERVER confirms non-existence — a
 * cache-only miss (cold start / incognito, before the server snapshot) stays
 * 'loading' rather than flipping to 'missing'.
 *
 * 'missing' is the trigger for the EnsureUserProfile guard: it runs
 * ensureUserProfile to recover from an interrupted signup. Gating it on a
 * server-confirmed miss stops a transient cold-cache snapshot from spuriously
 * triggering recovery on every page load.
 */

import { useEffect, useState } from 'react';

import { subscribeDoc, userRef } from '@/lib/firestore';
import type { UserProfile } from '@/schemas';

export type UserProfileState =
  | { state: 'loading' }
  | { state: 'missing' }
  | { state: 'present'; profile: UserProfile & { id: string } };

export function useUserProfile(uid: string | null): UserProfileState {
  const [state, setState] = useState<UserProfileState>({ state: 'loading' });

  useEffect(() => {
    if (!uid) {
      setState({ state: 'missing' });
      return;
    }
    setState({ state: 'loading' });
    const unsub = subscribeDoc<UserProfile>(userRef(uid), (data, meta) => {
      if (data !== null) {
        setState({ state: 'present', profile: data });
        return;
      }
      // Non-existence is only authoritative from the server. A cache-only miss
      // (cold start / incognito, before the server snapshot arrives) must NOT
      // be reported as 'missing' — it would spuriously trip EnsureUserProfile's
      // recovery on every cold load. Stay 'loading' until the server confirms.
      if (meta.fromCache) return;
      setState({ state: 'missing' });
    });
    return unsub;
  }, [uid]);

  return state;
}
